const ISO_ALPHA2_CODES = [
  "AD","AE","AF","AG","AI","AL","AM","AO","AQ","AR","AS","AT","AU","AW","AX","AZ",
  "BA","BB","BD","BE","BF","BG","BH","BI","BJ","BL","BM","BN","BO","BQ","BR","BS","BT","BV",
  "BW","BY","BZ","CA","CC","CD","CF","CG","CH","CI","CK","CL","CM","CN","CO","CR","CU","CV","CW","CX",
  "CY","CZ","DE","DJ","DK","DM","DO","DZ","EC","EE","EG","EH","ER","ES","ET","FI","FJ","FK","FM","FO",
  "FR","GA","GB","GD","GE","GF","GG","GH","GI","GL","GM","GN","GP","GQ","GR","GS","GT","GU","GW","GY",
  "HK","HM","HN","HR","HT","HU","ID","IE","IL","IM","IN","IO","IQ","IR","IS","IT","JE","JM","JO","JP",
  "KE","KG","KH","KI","KM","KN","KP","KR","KW","KY","KZ","LA","LB","LC","LI","LK","LR","LS","LT","LU",
  "LV","LY","MA","MC","MD","ME","MF","MG","MH","MK","ML","MM","MN","MO","MP","MQ","MR","MS","MT","MU",
  "MV","MW","MX","MY","MZ","NA","NC","NE","NF","NG","NI","NL","NO","NP","NR","NU","NZ","OM","PA","PE",
  "PF","PG","PH","PK","PL","PM","PN","PR","PS","PT","PW","PY","QA","RE","RO","RS","RU","RW","SA","SB",
  "SC","SD","SE","SG","SH","SI","SJ","SK","SL","SM","SN","SO","SR","SS","ST","SV","SX","SY","SZ","TC",
  "TD","TF","TG","TH","TJ","TK","TL","TM","TN","TO","TR","TT","TV","TW","TZ","UA","UG","UM","US","UY",
  "UZ","VA","VC","VE","VG","VI","VN","VU","WF","WS","YE","YT","ZA","ZM","ZW","XK"
];

const ISO_ALPHA2_SET = new Set(ISO_ALPHA2_CODES);

const hasDisplayNames =
  typeof Intl !== "undefined" && typeof Intl.DisplayNames === "function";

const displayUk = hasDisplayNames
  ? new Intl.DisplayNames(["uk"], { type: "region" })
  : null;
const displayEn = hasDisplayNames
  ? new Intl.DisplayNames(["en"], { type: "region" })
  : null;

const ukCollator =
  typeof Intl !== "undefined" && typeof Intl.Collator === "function"
    ? new Intl.Collator("uk", { sensitivity: "base" })
    : null;

function safeDisplay(display, code) {
  if (!display) return null;
  try {
    const value = display.of(code);
    if (!value || value === code) return null;
    return value;
  } catch {
    return null;
  }
}

export const flagEmoji = (code) =>
  (code || "")
    .toUpperCase()
    .replace(/./g, (char) => String.fromCodePoint(127397 + char.charCodeAt(0)));

const countries = ISO_ALPHA2_CODES.map((code) => {
  const nameUk = safeDisplay(displayUk, code);
  const nameEn = safeDisplay(displayEn, code);
  return {
    code,
    nameUk: nameUk || nameEn || code,
    nameEn: nameEn || nameUk || code,
    flag: flagEmoji(code),
  };
});

countries.sort((a, b) => {
  if (ukCollator) {
    return ukCollator.compare(a.nameUk, b.nameUk);
  }
  return a.nameUk.localeCompare(b.nameUk);
});

export const COUNTRIES = countries;
export const COUNTRY_CODES = ISO_ALPHA2_CODES;
export const COUNTRIES_BY_CODE = countries.reduce((acc, item) => {
  acc[item.code] = item;
  return acc;
}, {});

const COUNTRIES_BY_NAME = countries.reduce((acc, item) => {
  const uk = item.nameUk.toLowerCase();
  const en = item.nameEn.toLowerCase();
  if (!acc[uk]) acc[uk] = item;
  if (!acc[en]) acc[en] = item;
  return acc;
}, {});

function normalizeCode(code) {
  if (code == null) return null;
  const value = String(code).trim().toUpperCase();
  if (value.length !== 2) return null;
  if (ISO_ALPHA2_SET.has(value)) return value;
  return value;
}

function normalizeNameKey(name) {
  if (!name || typeof name !== "string") return "";
  return name.trim().toLowerCase();
}

const ARTICLES_RE = /^(the|la|le|los|las|el)\s+/i;

function findByName(name) {
  if (!name || typeof name !== "string") return null;
  const key = normalizeNameKey(name);
  if (!key) return null;
  if (COUNTRIES_BY_NAME[key]) return COUNTRIES_BY_NAME[key];

  const withoutArticle = key.replace(ARTICLES_RE, "").trim();
  if (withoutArticle && COUNTRIES_BY_NAME[withoutArticle]) {
    return COUNTRIES_BY_NAME[withoutArticle];
  }

  return null;
}

export function resolveCountryFlag(source) {
  if (!source) return "";
  if (typeof source === "string") {
    const normalized = normalizeCode(source);
    if (normalized) {
      const known = COUNTRIES_BY_CODE[normalized];
      return known ? known.flag : flagEmoji(normalized);
    }
    const byName = findByName(source);
    return byName ? byName.flag : "";
  }

  if (typeof source === "object") {
    if (typeof source.flag === "string" && source.flag) {
      return source.flag;
    }

    const candidate =
      source.country_code ||
      source.countryCode ||
      source.iso_code ||
      source.isoCode ||
      source.code ||
      source.country_iso ||
      source.countryIso ||
      source.alpha2 ||
      null;

    const normalized = normalizeCode(candidate);
    if (normalized) {
      const known = COUNTRIES_BY_CODE[normalized];
      return known ? known.flag : flagEmoji(normalized);
    }

    const byName =
      findByName(source.country) ||
      findByName(source.name) ||
      findByName(source.country_name);
    return byName ? byName.flag : "";
  }

  return "";
}

export function getCountryMeta(codeOrName) {
  if (!codeOrName) return null;
  const normalized = normalizeCode(codeOrName);
  if (normalized && COUNTRIES_BY_CODE[normalized]) {
    return COUNTRIES_BY_CODE[normalized];
  }
  return findByName(codeOrName);
}

export default COUNTRIES;
